import { useState } from 'react'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'
import { handleApiError } from '@/api/client'
import type { PlayAccountOption } from '@/types'

interface EmailLoginFormProps {
  userType: 'student' | 'parent'
  onSuccess: () => void
}

export function EmailLoginForm({ userType, onSuccess }: EmailLoginFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [accountSelect, setAccountSelect] = useState<{
    accounts: PlayAccountOption[]
    selectToken: string
  } | null>(null)
  const [selectLoading, setSelectLoading] = useState(false)
  const login = useAuthStore((s) => s.login)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await authApi.login(email, password, userType)
      if (response.type === 'ACCOUNT_SELECT' && response.accounts && response.selectToken) {
        setAccountSelect({ accounts: response.accounts, selectToken: response.selectToken })
      } else if (response.auth) {
        login(response.auth)
        onSuccess()
      }
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleAccountSelect = async (source: string) => {
    if (!accountSelect) return
    setError('')
    setSelectLoading(true)

    try {
      const response = await authApi.selectAccount(accountSelect.selectToken, source)
      login(response)
      onSuccess()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setSelectLoading(false)
    }
  }

  if (accountSelect) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <p className="text-sm text-gray-600">두 개의 계정이 있습니다.</p>
          <p className="text-sm text-gray-500">사용할 계정을 선택하세요.</p>
        </div>
        {accountSelect.accounts.map((account) => (
          <button
            key={account.source}
            onClick={() => handleAccountSelect(account.source)}
            disabled={selectLoading}
            className="w-full py-3 px-4 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
          >
            <div className="text-sm font-medium text-gray-900">{account.displayName}</div>
            <div className="text-xs text-gray-500">
              {account.source === 'Q3' ? '깨봉 러닝센터 계정' : '깨봉 온라인 계정'}
            </div>
          </button>
        ))}
        {error && <p className="text-sm text-red-500">{error}</p>}
        <button
          onClick={() => setAccountSelect(null)}
          className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          돌아가기
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <input
          type={userType === 'student' ? 'text' : 'email'}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={userType === 'student' ? '아이디' : '이메일'}
          className="input"
          required
          autoComplete={userType === 'student' ? 'username' : 'email'}
        />
      </div>
      <div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="비밀번호"
          className="input"
          required
          autoComplete="current-password"
        />
      </div>
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 text-base"
      >
        {loading ? '로그인 중...' : '로그인'}
      </button>
    </form>
  )
}
